import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { AIInsightTooltip } from './ai-insight-tooltip';
import { cn } from '@/lib/utils';

interface AssetsDueForReviewCardProps {
  totalCount: number;
  depreciationCount: number;
  insuranceCount: number;
  warrantyCount: number;
  onClick?: () => void;
}

export function AssetsDueForReviewCard({
  totalCount,
  depreciationCount,
  insuranceCount,
  warrantyCount,
  onClick,
}: AssetsDueForReviewCardProps): React.ReactElement {
  const kpiData = {
    id: '4',
    label: 'Assets Due for Review',
    value: totalCount,
    icon: 'AlertCircle',
  };

  return (
    <Card 
      className={cn(
        "relative overflow-hidden border-l-4 border-l-yellow-500",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full -mr-16 -mt-16" />
      <CardContent className="relative p-6">
        <div className="flex flex-col">
          {/* Top section - Icon and label */}
          <div className="flex items-center gap-2 mb-3">
            <div className="p-2 rounded-full bg-yellow-50 dark:bg-yellow-950">
              <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">Assets Due for Review</p>
            {onClick && (
              <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
            )}
          </div>
          
          {/* Middle section - Main value and pills inline */}
          <div className="flex items-center justify-between gap-4 mb-2">
            <h3 className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{totalCount.toLocaleString('en-GB')}</h3>
            
            {/* Pill Widgets inline with main value */}
            <div className="flex flex-wrap gap-2 justify-end">
              <Badge 
                variant="outline" 
                className="justify-between px-2.5 py-1 bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
              >
                <span className="text-[10px] font-medium">Depreciation &gt; £1k</span>
                <span className="ml-1.5 font-bold text-xs">{depreciationCount}</span>
              </Badge>
              
              <Badge 
                variant="outline" 
                className="justify-between px-2.5 py-1 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 whitespace-nowrap"
              >
                <span className="text-[10px] font-medium">Insurance Expiring</span>
                <span className="ml-1.5 font-bold text-xs">{insuranceCount}</span>
              </Badge>
              
              <Badge 
                variant="outline" 
                className="justify-between px-2.5 py-1 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 whitespace-nowrap"
              >
                <span className="text-[10px] font-medium">Warranty Expiring</span>
                <span className="ml-1.5 font-bold text-xs">{warrantyCount}</span>
              </Badge>
            </div>
          </div>
          
          {/* Bottom section - Subtitle */}
          <p className="text-sm text-muted-foreground">
            Require attention
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
